'use client';

import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';

import { SettingsSection } from '@/core/components/settings/settings-section';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

enum EmailChangeStep {
  DISPLAY = 'display',
  ENTER_EMAIL = 'enter-email',
  EMAIL_SENT = 'email-sent',
}

interface ContactSectionProps {
  sectionId: string;
  email: string;
  onEmailChange: (newEmail: string) => Promise<void>;
  authDict: {
    emailLabel: string;
    emailPlaceholder: string;
    changeEmail: string;
  };
  settingsDict: {
    contact: {
      title: string;
      description: string;
      emailInfo: string;
      newEmailLabel: string;
      emailSentInfo: string;
    };
    cancel: string;
    continue: string;
    sending: string;
  };
  getErrorMessage: (code?: string) => string;
}

export function ContactSection({
  sectionId,
  email,
  onEmailChange,
  authDict,
  settingsDict,
  getErrorMessage,
}: ContactSectionProps) {
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeStep, setEmailChangeStep] = useState<EmailChangeStep>(
    EmailChangeStep.DISPLAY,
  );
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleStartEmailChange = () => {
    setEmailChangeStep(EmailChangeStep.ENTER_EMAIL);
    setNewEmail('');
    setEmailError(null);
  };

  const handleCancelEmailChange = () => {
    setEmailChangeStep(EmailChangeStep.DISPLAY);
    setNewEmail('');
    setEmailError(null);
  };

  const handleSendVerificationCode = async () => {
    if (!newEmail) return;

    setIsSendingCode(true);
    setEmailError(null);

    try {
      await onEmailChange(newEmail);
      setEmailChangeStep(EmailChangeStep.EMAIL_SENT);
    } catch (err) {
      setEmailError(getErrorMessage(getApiValidationErrorCode(err, 'email')));
    } finally {
      setIsSendingCode(false);
    }
  };

  return (
    <SettingsSection
      id={sectionId}
      title={settingsDict.contact.title}
      footer={settingsDict.contact.description}
      grouped
    >
      <div className="space-y-4 px-4 py-3.5 sm:px-5">
        {emailError ? (
          <Alert variant="destructive">
            <AlertDescription>{emailError}</AlertDescription>
          </Alert>
        ) : null}

        {emailChangeStep === EmailChangeStep.DISPLAY ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="email">{authDict.emailLabel}</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input id="email" value={email} readOnly className="bg-muted" />
                <Button variant="outline" onClick={handleStartEmailChange}>
                  {authDict.changeEmail}
                </Button>
              </div>
            </div>
            <p className="text-[12px] leading-snug text-muted-foreground/85">
              {settingsDict.contact.emailInfo}
            </p>
          </div>
        ) : null}

        {emailChangeStep === EmailChangeStep.ENTER_EMAIL ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newEmail">
                {settingsDict.contact.newEmailLabel}
              </Label>
              <Input
                id="newEmail"
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={authDict.emailPlaceholder}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelEmailChange}>
                {settingsDict.cancel}
              </Button>
              <Button
                onClick={() => void handleSendVerificationCode()}
                disabled={!newEmail || isSendingCode}
                isLoading={isSendingCode}
              >
                {settingsDict.continue}
              </Button>
            </div>
          </div>
        ) : null}

        {emailChangeStep === EmailChangeStep.EMAIL_SENT ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                {settingsDict.contact.emailSentInfo.replace(
                  '{email}',
                  newEmail,
                )}
              </AlertDescription>
            </Alert>
            <Button variant="outline" onClick={handleCancelEmailChange}>
              {settingsDict.cancel}
            </Button>
          </div>
        ) : null}
      </div>
    </SettingsSection>
  );
}
